import {
  browserName,
  fullBrowserVersion,
  isMobile,
  osName,
  osVersion,
} from 'react-device-detect';
import { Device, StepData, TrackingStep } from './types/trackingDataModel';
import { PageProps } from '../../formBuilder/usePages';
import { TRACKING_STEP_TYPES } from './constants/constants';
import { ACTION_ENUM } from '../../../components/@material/navigationButtons/types';

export const getDeviceOrientation = (): string => {
  const orientation = window.screen.orientation?.type;
  if (
    orientation === 'landscape-primary' ||
    orientation === 'landscape-secondary' ||
    orientation === 'portrait-primary' ||
    orientation === 'portrait-secondary'
  ) {
    return orientation;
  }

  const angle = window.screen.orientation.angle;
  const width = window.innerWidth;
  const height = window.innerHeight;
  const isLandscape = width > height;

  switch (angle) {
    case 0:
      return isLandscape ? 'landscape-primary' : 'portrait-primary';
    case 180:
      return isLandscape ? 'landscape-secondary' : 'portrait-secondary';
    case 90:
      return isLandscape ? 'landscape-primary' : 'portrait-secondary';
    case -90:
      return isLandscape ? 'landscape-secondary' : 'portrait-primary';
    default:
      return isLandscape ? 'landscape-primary' : 'portrait-primary';
  }
};

export const getDeviceInfo = (): Partial<Device> => ({
  deviceType: isMobile ? 'mobile' : 'desktop',
  deviceOS: osName,
  deviceOSVersion: osVersion,
  deviceBrowser: browserName,
  deviceBrowserVersion: fullBrowserVersion,
  deviceScreen: {
    width: window.screen.width,
    height: window.screen.height,
    pixelRatio: window.devicePixelRatio || 1,
    orientation: getDeviceOrientation(),
  },
  userAgent: navigator.userAgent,
});

export const extractStepFromPathname = (pathname: string): number | null => {
  const segments = pathname.split('/').filter(Boolean);
  const last = segments.at(-1);
  const match = last?.match(/^P(\d+)$/i);
  return match ? parseInt(match[1], 10) : -1;
};

export const resolveStepType = (
  step: StepData,
  currentSteps: TrackingStep[]
): TrackingStep['stepType'] | null => {
  const isFirstStep = currentSteps.length === 0;

  if (isFirstStep)
    return step.isResume
      ? TRACKING_STEP_TYPES.RESUME
      : TRACKING_STEP_TYPES.START;

  switch (step.actionTaken) {
    case ACTION_ENUM.previous:
      return TRACKING_STEP_TYPES.BACK;
    case ACTION_ENUM.submit:
      return TRACKING_STEP_TYPES.SUBMIT;
    case ACTION_ENUM.next:
      return TRACKING_STEP_TYPES.NEXT;
    default:
      return null;
  }
};

export function buildStepData(
  activeStep: number,
  pages: PageProps[],
  action: string | undefined,
  hasSARId: boolean | undefined = false
): StepData | null {
  if (activeStep < 0 || !pages[activeStep]) return null;

  const page = pages[activeStep];
  return {
    index: activeStep,
    name: page.label || page.description || `P-${activeStep}`,
    visitedOn: new Date().toISOString(),
    actionTaken: action,
    isResume: hasSARId,
  };
}

export const resolvePageStepperNavigationAction = (
  currentStep: number,
  targetStep: number
): ACTION_ENUM | undefined => {
  if (targetStep === currentStep) return undefined;
  return targetStep < currentStep ? ACTION_ENUM.previous : ACTION_ENUM.next;
};
